import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import en from './en.json';
import { consent, preferenceStorage } from '../consent/consent.js';

/**
 * Translations.
 *
 * English is the default because that is what the product ships in; Turkish is
 * a choice the merchant makes, remembered per browser. A missing key falls back
 * to English and then to the key itself, so a partially translated locale
 * degrades to readable text rather than to blanks, the app is only partly
 * translated today and is migrating string by string.
 */

export const LOCALES = { en: 'English', tr: 'Türkçe' } as const;
export type Locale = keyof typeof LOCALES;

/*
 * Where the merchant is, which is a different question from what they read.
 *
 * The language decides the words; the region decides how a date, a number and
 * a sum of money are written — "1,234.56" against "1.234,56", "Oct 5" against
 * "5 Eki". Someone can run their shop from Istanbul and prefer the product in
 * English, and before this they had to take American thousands separators
 * with it.
 *
 * The names are in each region's own language, because that is what makes a
 * list of places scannable to someone who does not read the page's language.
 */
export const REGIONS = {
  TR: 'Türkiye',
  US: 'United States',
  GB: 'United Kingdom',
  DE: 'Deutschland',
  FR: 'France',
  ES: 'España',
  NL: 'Nederland',
  AE: 'الإمارات',
} as const;
export type Region = keyof typeof REGIONS;

/* The region a language is at home in, and the default for anyone who has not
   said otherwise — so the formatting is exactly what it was before regions
   existed, rather than something new on a screen nobody asked to change. */
const HOME_REGION: Record<Locale, Region> = { en: 'US', tr: 'TR' };

const STORAGE_KEY = 'krevya.locale';
const REGION_KEY = 'krevya.region';
/*
 * English ships with the page; Turkish is fetched the first time it is needed.
 * Every visitor used to download both, 365KB of text before the landing page
 * could run, when most of them only ever read one. Until Turkish arrives its
 * entry is empty, and a lookup falls back to English as it does for any
 * missing key; `loadCatalogue` is awaited before Turkish is ever shown.
 */
const CATALOGUES: Record<Locale, Record<string, string>> = { en, tr: {} };
const loading = new Map<Locale, Promise<void>>();

/** Fetches a language's strings once; resolves at once for English. */
export function loadCatalogue(locale: Locale): Promise<void> {
  if (locale === 'en') return Promise.resolve();
  let pending = loading.get(locale);
  if (!pending) {
    pending = import('./tr.json').then(
      (module) => {
        CATALOGUES.tr = module.default;
      },
      (error: unknown) => {
        // Forgotten, so the next attempt asks again rather than reusing a failure.
        loading.delete(locale);
        throw error;
      },
    );
    loading.set(locale, pending);
  }
  return pending;
}

type Translate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * The template for a key, with its one-thing form when there is one.
 *
 * A string carrying `{count}` read "1 ad sets" when there was one, because the
 * catalogue held a single form and callers had to remember to pick a `.one`
 * key themselves — thirteen did, the Ads tree did not. This does it for every
 * caller: a count of exactly one takes `<key>.one` if that language has it.
 *
 * Only the language's own `.one` is used. Turkish does not inflect a noun after
 * a number ("1 reklam seti", "2 reklam seti"), so it defines none, and falling
 * back to the English one-form would put English on a Turkish screen. Callers
 * that already ask for `.one` by name get the same string as before.
 */
export function templateFor(
  catalogues: Record<Locale, Record<string, string>>,
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const one = vars && String(vars['count']) === '1' ? catalogues[locale][`${key}.one`] : undefined;
  return one ?? catalogues[locale][key] ?? catalogues.en[key] ?? key;
}

const LocaleContext = createContext<{
  locale: Locale;
  setLocale: (locale: Locale) => void;
  region: Region;
  setRegion: (region: Region) => void;
  /** What `Intl` is given: the language to speak, in the region's conventions. */
  formatLocale: string;
  t: Translate;
}>({
  locale: 'en',
  setLocale: () => undefined,
  region: 'US',
  setRegion: () => undefined,
  formatLocale: 'en-US',
  t: (key) => key,
});

/*
 * The language and the region are preferences (see storage-inventory.ts), so
 * they are kept where the consent module says: localStorage once preferences
 * are allowed, the tab's sessionStorage until then.
 */
/** The language this browser chose last time, English when it never chose. */
export function readStoredLocale(): Locale {
  try {
    const stored = preferenceStorage().getItem(STORAGE_KEY);
    if (stored && stored in CATALOGUES) return stored as Locale;
  } catch {
    // Private browsing: fall through to the default.
  }
  return 'en';
}

function readStoredRegion(): Region | null {
  try {
    const stored = preferenceStorage().getItem(REGION_KEY);
    if (stored && stored in REGIONS) return stored as Region;
  } catch {
    // Private browsing: there is a sensible default below.
  }
  return null;
}

/**
 * A string in the language the page is in, for code that is not a component.
 *
 * `messageForCode` is called from 110 places, many of them in plain functions
 * and catch blocks where there is no hook to reach for, so it was English on
 * every screen. The document's `lang` is kept in step with the chosen language
 * by `LocaleProvider` below, so it is the one place outside React that knows.
 * Null when the key has no entry, so a caller can fall back to its own text.
 */
export function translateNow(key: string): string | null {
  const lang = typeof document === 'undefined' ? 'en' : document.documentElement.lang;
  const locale: Locale = lang in CATALOGUES ? (lang as Locale) : 'en';
  return CATALOGUES[locale][key] ?? null;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);
  const [chosenRegion, setRegionState] = useState<Region | null>(readStoredRegion);

  // The document ships with a hardcoded lang, and only a *change* of locale
  // used to correct it, so a first load sat on the wrong one. That is not
  // cosmetic: CSS `text-transform: uppercase` is locale-aware, and English
  // labels uppercased under a Turkish document turned every "i" into "İ"
  // ("SİGNAL İT CAME FROM"). Screen readers pick the wrong voice from it too.
  //
  // Written only when it differs: setting it, even to the same value, makes the
  // browser re-check every element on the page against `:lang()`, and on the
  // prerendered landing page that redrew the whole first screen for nothing.
  useEffect(() => {
    if (document.documentElement.lang !== locale) document.documentElement.lang = locale;
  }, [locale]);

  /*
   * The first read happens before the privacy region is known, when only the
   * tab's storage may be read. Once the answer allows preferences (a saved
   * yes, or the US model's default) the persistent choice is read again, so
   * a returning visitor gets their language back, once its strings are here.
   */
  useEffect(
    () =>
      consent.subscribe(() => {
        if (!consent.allows('preferences')) return;
        const stored = readStoredLocale();
        if (stored !== 'en') {
          loadCatalogue(stored).then(
            () => setLocaleState(stored),
            () => undefined,
          );
        }
        setRegionState((current) => readStoredRegion() ?? current);
      }),
    [],
  );

  // The choice is remembered at once, as it always was; the screen switches
  // when the language's strings are here, so it changes once, straight into
  // Turkish, rather than into English labelled as Turkish.
  const setLocale = useCallback((next: Locale) => {
    try {
      preferenceStorage().setItem(STORAGE_KEY, next);
    } catch {
      // Remembering the choice is a convenience, not a requirement.
    }
    loadCatalogue(next).then(
      () => {
        setLocaleState(next);
        document.documentElement.lang = next;
      },
      () => {
        // Offline: the page stays in the language it is in, and the control
        // works again once the strings can be fetched.
      },
    );
  }, []);

  const setRegion = useCallback((next: Region) => {
    setRegionState(next);
    try {
      preferenceStorage().setItem(REGION_KEY, next);
    } catch {
      // Remembering the choice is a convenience, not a requirement.
    }
  }, []);

  const t = useCallback<Translate>(
    (key, vars) => {
      const template = templateFor(CATALOGUES, locale, key, vars);
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        name in vars ? String(vars[name]) : match,
      );
    },
    [locale],
  );

  // Nobody has chosen: the language's home region, which formats exactly as
  // the language alone used to.
  const region = chosenRegion ?? HOME_REGION[locale];

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      region,
      setRegion,
      formatLocale: `${locale}-${region}`,
      t,
    }),
    [locale, setLocale, region, setRegion, t],
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}

export function useT(): Translate {
  return useContext(LocaleContext).t;
}
